import * as SelectPrimitive from '@radix-ui/react-select'
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, Search } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

function Select({ ...props }) {
  return <SelectPrimitive.Root data-slot="select" {...props} />
}

function SelectGroup({ ...props }) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />
}

function SelectValue({ ...props }) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />
}

function SelectTrigger({ className, size = 'default', children, ...props }) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex w-fit items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon className="size-4 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
}

function SelectContent({ className, children, position = 'popper', searchable = false, ...props }) {
  const [searchQuery, setSearchQuery] = React.useState('')
  const inputRef = React.useRef(null)

  // Focus the search input when the content opens
  React.useEffect(() => {
    if (searchable && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus()
      }, 0)
    }
  }, [searchable])

  // Filter children based on search query
  const filteredChildren = React.useMemo(() => {
    if (!searchable || !searchQuery) return children

    return React.Children.map(children, (child) => {
      // Handle SelectGroup case
      if (child?.type === SelectGroup) {
        const filteredGroupChildren = React.Children.toArray(child.props.children).filter(
          (groupChild) => {
            if (groupChild?.type === SelectItem) {
              const itemText = groupChild.props.children?.toString().toLowerCase()
              return itemText?.includes(searchQuery.toLowerCase())
            }
            return true // Keep non-SelectItem children (like labels)
          }
        )

        // Only return the group if it has matching children
        if (filteredGroupChildren.length > 0) {
          return React.cloneElement(child, {
            ...child.props,
            children: filteredGroupChildren,
          })
        }
        return null
      }

      // Handle SelectItem case
      if (child?.type === SelectItem) {
        const itemText = child.props.children?.toString().toLowerCase()
        if (itemText?.includes(searchQuery.toLowerCase())) {
          return child
        }
        return null
      }

      return child // Keep other components unchanged
    })
  }, [children, searchQuery, searchable])

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        className={cn(
          'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-(--radix-select-content-available-height) min-w-[8rem] origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border shadow-md',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className
        )}
        position={position}
        onFocusOutside={(event) => {
          // Keep the content open when the input steals focus
          event.preventDefault()
        }}
        onCloseAutoFocus={(event) => {
          // Prevent Radix from moving focus back to the trigger and closing
          event.preventDefault()
        }}
        {...props}
      >
        <SelectScrollUpButton />
        {searchable && (
          <div className="sticky top-0 z-10 bg-popover px-2 py-1 border-b">
            <div className="flex items-center px-1 border rounded-md focus-within:ring-1 focus-within:ring-ring">
              <Search className="w-4 h-4 mr-2 opacity-50" />
              <input
                ref={inputRef}
                type="text" // always text
                inputMode="text" // allow letters & numbers
                autoComplete="off"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search…"
                className="w-full py-2 text-sm bg-transparent border-none outline-none"
                onKeyDownCapture={(e) => e.stopPropagation()}
                // onKeyPressCapture={(e) => e.stopPropagation()}
                onKeyUpCapture={(e) => e.stopPropagation()}
                onBeforeInputCapture={(e) => e.stopPropagation()}
                // onInputCapture={(e) => e.stopPropagation()}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="p-1 rounded-full hover:bg-accent"
                >
                  <span className="text-sm">×</span>
                </button>
              )}
            </div>
          </div>
        )}
        <SelectPrimitive.Viewport
          className={cn(
            'p-1',
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1'
          )}
        >
          {filteredChildren}
          {searchable &&
            Array.isArray(filteredChildren) &&
            filteredChildren.filter(Boolean).length === 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground">No results found</div>
            )}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
}

function SelectLabel({ className, ...props }) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn('text-muted-foreground px-2 py-1.5 text-xs', className)}
      {...props}
    />
  )
}

function SelectItem({ className, children, ...props }) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className
      )}
      {...props}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
}

function SelectSeparator({ className, ...props }) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn('bg-border pointer-events-none -mx-1 my-1 h-px', className)}
      {...props}
    />
  )
}

function SelectScrollUpButton({ className, ...props }) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  )
}

function SelectScrollDownButton({ className, ...props }) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  )
}

// Create a new SearchableSelect component that combines Select with searchable SelectContent
function SearchableSelect({ children, ...props }) {
  return <Select {...props}>{children}</Select>
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  SearchableSelect,
}
